import React from 'react';
import { ShieldCheck, Lock, FileText, AlertCircle, ArrowLeft } from 'lucide-react';
import { useApp } from '../../context/AppContext';

export const PrivacyPolicyPage: React.FC = () => {
  const { navigateTo } = useApp();

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8 space-y-8">
      {/* Header Container */}
      <div className="max-w-4xl mx-auto space-y-3">
        <button
          onClick={() => navigateTo('home')}
          className="text-xs text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1 font-semibold cursor-pointer"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Homepage</span>
        </button>

        <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-10 border border-slate-800 shadow-xl space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
            <Lock className="w-3.5 h-3.5 text-emerald-400" />
            <span>NDPA 2023 Compliance Framework</span>
          </div>
          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-white">
            Privacy Policy
          </h1>
          <p className="text-xs sm:text-sm text-slate-300">
            Last updated: September 2024. Applicable to all users of JobMatch NG in Nigeria.
          </p>
        </div>
      </div>

      {/* Legal Advisory Notice */}
      <div className="max-w-4xl mx-auto bg-amber-50 border border-amber-200 rounded-2xl p-4 text-xs text-amber-900 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <p className="leading-relaxed">
          <strong>Legal Disclaimer:</strong> This privacy policy is published for general platform operational
          transparency and explains how personal information is handled on JobMatch NG under the principles
          of the Nigeria Data Protection Act (NDPA 2023). It should be reviewed by qualified Nigerian legal counsel.
        </p>
      </div>

      {/* Main Legal Content */}
      <div className="max-w-4xl mx-auto bg-white rounded-3xl p-6 sm:p-12 border border-slate-200 shadow-xs space-y-8 text-xs sm:text-sm text-slate-700 leading-relaxed">
        {/* Section 1 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">1.</span> Introduction & Scope
          </h2>
          <p>
            JobMatch NG (“we,” “our,” or “the Platform”) operates a technology marketplace connecting customers
            in Nigeria who require skilled manual, technical, and trade services with verified professionals and
            technicians. This Privacy Policy details how we collect, store, utilize, disclose, and safeguard
            personal data when you visit our website, register an account, post a repair request, or submit
            service quotations.
          </p>
        </section>

        {/* Section 2 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">2.</span> Data Controller & Governing Law
          </h2>
          <p>
            JobMatch NG acts as the data controller regarding personal information processed through the Platform.
            All personal data operations are conducted in accordance with the <strong>Nigeria Data Protection Act (NDPA 2023)</strong>{' '}
            and associated regulations issued by the Nigeria Data Protection Commission (NDPC).
          </p>
          <p>
            Contact for Data Protection Inquiries: <strong className="text-slate-900">support@jobmatch.ng</strong>
          </p>
        </section>

        {/* Section 3 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">3.</span> Information We Collect
          </h2>
          <div className="space-y-3 pl-2">
            <div>
              <h3 className="font-bold text-slate-900">A. Customer Information:</h3>
              <ul className="list-disc pl-5 space-y-1 mt-1 text-slate-600">
                <li>Personal identification: Full name, active email address, and phone number.</li>
                <li>Geographic data: Nigerian State, Local Government Area (LGA), and general area/landmark.</li>
                <li>Job request details: Description of defect/project, urgency level, and user-uploaded media (photos, videos).</li>
                <li>Platform interactions: Quotes received, quotes accepted, and ratings/reviews submitted.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900">B. Professional Information:</h3>
              <ul className="list-disc pl-5 space-y-1 mt-1 text-slate-600">
                <li>Professional profile: Full legal name, trade business name, primary and secondary trade skills, years of experience, hourly/diagnostic pricing.</li>
                <li>Geographic coverage: Registered Nigerian State, home LGA, and designated active service LGAs.</li>
                <li>Verification documents: National Identification Number (NIN), driver’s license, international passport, voter's card, and utility bill images.</li>
                <li>Biometric liveness data: Real-time facial scan frames used strictly for verifying identity against the government ID.</li>
                <li>Transaction and payout records: Paystack transaction references, escrow deposits, and bank payout timestamps.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">4.</span> Purposes & Legal Grounds for Processing
          </h2>
          <p>We process personal data on the following lawful grounds recognized under the NDPA:</p>
          <ul className="list-disc pl-5 space-y-1 text-slate-600">
            <li><strong>Contractual Necessity:</strong> Facilitating job matching, notifications, itemized quotations, and in-app communications between customers and professionals.</li>
            <li><strong>Legitimate Interest & Safety:</strong> Authenticating professional identities through government ID and facial liveness to protect platform participants from fraud.</li>
            <li><strong>Legal Obligation:</strong> Maintaining accounting, payment transaction records, and cooperating with statutory Nigerian law enforcement authorities when formally ordered.</li>
            <li><strong>Consent:</strong> Sending service updates, marketing communications, and optional notifications via email or WhatsApp.</li>
          </ul>
        </section>

        {/* Section 5 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">5.</span> Data Sharing & Disclosure
          </h2>
          <p>
            JobMatch NG does <em>not</em> sell or rent your personal data to advertisers. Information is only
            shared under the following limited circumstances:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-slate-600">
            <li>
              <strong>Between Marketplace Participants:</strong> When a customer accepts a professional’s quote,
              the professional’s contact details and the customer’s phone number and job location are exchanged
              to enable site coordination. Prior to acceptance, direct contact info remains masked.
            </li>
            <li>
              <strong>Payment Gateways:</strong> Customer job escrow payments and professional payouts are processed directly by{' '}
              <strong>Paystack Payments Limited</strong> (PCI-DSS certified). JobMatch NG never stores or processes
              raw debit card numbers, CVVs, or bank PINs on its servers.
            </li>
            <li>
              <strong>Cloud Infrastructure:</strong> Database, image storage, and hosting services located in secure
              data center environments with encryption in transit and at rest.
            </li>
          </ul>
        </section>

        {/* Section 6 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">6.</span> Data Security & Retention
          </h2>
          <p>
            We implement administrative, technical, and physical safeguards designed to protect personal data against
            unauthorized access, accidental loss, destruction, or disclosure. All sensitive communication utilizes
            HTTPS/TLS encryption.
          </p>
          <p>
            We retain account data for the duration that your account is active. Verification records are retained
            for audit and fraud prevention purposes in accordance with Nigerian statutory limitation periods.
          </p>
        </section>

        {/* Section 7 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">7.</span> Your Rights Under the NDPA 2023
          </h2>
          <p>As a Nigerian data subject, you hold the following rights regarding your personal information:</p>
          <ul className="list-disc pl-5 space-y-1 text-slate-600">
            <li>The right to be informed about how your personal data is collected and used.</li>
            <li>The right of access to the personal data we hold about you.</li>
            <li>The right to rectify inaccurate or incomplete records.</li>
            <li>The right to request erasure of your data, subject to legal compliance retentions.</li>
            <li>The right to object to or restrict processing on legitimate grounds.</li>
          </ul>
          <p className="pt-1">
            To exercise any of these rights, email our Data Protection Desk at{' '}
            <strong className="text-slate-900">support@jobmatch.ng</strong>.
          </p>
        </section>

        {/* Section 8 */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">8.</span> Minors & Children's Privacy
          </h2>
          <p>
            JobMatch NG is strictly intended for individuals aged 18 years and older. We do not knowingly solicit
            or collect personal information from minors. Accounts identified as belonging to individuals under 18
            will be terminated immediately.
          </p>
        </section>

        {/* Section 9 */}
        <section className="space-y-3 border-t border-slate-100 pt-6">
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span className="text-emerald-600">9.</span> Updates to This Policy
          </h2>
          <p>
            We may periodically revise this Privacy Policy to reflect technical enhancements, legislative updates,
            or operational changes. Material modifications will be indicated by revising the “Last updated” date
            at the top of this document. Continued use of JobMatch NG constitutes acceptance of revised terms.
          </p>
        </section>
      </div>
    </div>
  );
};
